/**
 * Delivery info — the fourth step of a tiffin order.
 *
 * How the food reaches the customer: pick & drop to an address, charged for
 * each meal it brings, or self pickup from the kitchen. The address is asked
 * for only for pick & drop. A note for the kitchen or the rider goes here
 * too.
 *
 * Continue is held until the server's quote describes what is on screen, so
 * the total carried to payment is the one that will be charged.
 */

import { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  formatMoney,
  tiffinFulfilmentLabel,
  validateDeliveryAddress,
  type FulfilmentType,
} from '@mfa/domain';
import { space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { ArrowIcon, Glyph } from '../../components/common/Icon';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { TIFFIN_STEPS } from '../../components/donation/CheckoutStepper';
import { Field } from '../../components/forms/Field';
import { RadioGroup } from '../../components/forms/Choice';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { SelectField } from '../../components/forms/SelectField';
import { Skeleton } from '../../components/feedback/Skeleton';
import { Text } from '../../components/common/Text';
import { TiffinTotalFooter, useTiffinCheckout } from './useTiffinCheckout';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function TiffinDeliveryScreen({ navigation }: DonorScreenProps<'TiffinDelivery'>) {
  const t = useT();
  const checkout = useTiffinCheckout(navigation);
  const { tiffin, draft, quote, total } = checkout;
  const { fulfilmentType, address, note } = draft;
  const [touched, setTouched] = useState(false);

  const cities = tiffin?.delivery.cities ?? [];
  const delivery = fulfilmentType === 'DOORSTEP_DELIVERY';
  const errors = delivery ? validateDeliveryAddress(address, cities) : {};
  const valid = Boolean(fulfilmentType) && Object.keys(errors).length === 0;
  const show = (message?: string) => (touched && message ? t(message) : null);

  const options = (tiffin?.fulfilmentTypes ?? []).map((type: FulfilmentType) => ({
    value: type,
    label: t(tiffinFulfilmentLabel[type]),
    description:
      type === 'DOORSTEP_DELIVERY'
        ? `${t('Delivered to your address')} · ${formatMoney(tiffin!.delivery.fee)} ${t('a meal')} · ${cities.join(', ')}`
        : tiffin?.pickup.address
          ? `${t('Collect from')} ${tiffin.pickup.address}`
          : t('Collect from our kitchen'),
    leading: <Glyph name={type === 'DOORSTEP_DELIVERY' ? 'delivery' : 'pickup'} size={22} />,
  }));

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-tiffin-delivery"
      subHeader={
        <CheckoutHeader
          current={3}
          steps={TIFFIN_STEPS}
          title={t('Delivery Info')}
          subtitle={t('Pick & drop to your door, or collect it yourself')}
        />
      }
      pinnedFooter={
        <TiffinTotalFooter checkout={checkout}>
          <Button
            label={t('Continue to Payment')}
            onPress={() => {
              setTouched(true);
              if (valid && total) navigation.navigate('TiffinPayment');
            }}
            disabled={!total || quote.isFetching}
            trailingIcon={<ArrowIcon />}
            testID="cta-tiffin-delivery-continue"
          />
        </TiffinTotalFooter>
      }
    >
      <Card title={t('How should we hand it over?')} testID="tiffin-route">
        {options.length ? (
          <RadioGroup
            testID="tiffin-fulfilment"
            options={options}
            value={fulfilmentType}
            onChange={draft.setFulfilment}
            label={t('Delivery option')}
          />
        ) : (
          <Skeleton height={64} />
        )}
        {fulfilmentType === 'SELF_PICKUP' && tiffin?.pickup.note ? (
          <Text variant="caption" tone="secondary" style={styles.caption}>
            {tiffin.pickup.note}
          </Text>
        ) : null}
      </Card>

      {delivery ? (
        <Card title={t('Delivery address')} style={styles.card} testID="tiffin-address">
          <View style={styles.fields}>
            <Field
              label={t('House and street')}
              required
              icon="location"
              value={address.addressLine}
              onChangeText={(value) => draft.setAddress({ addressLine: value })}
              placeholder={t('e.g. House 4, Street 9')}
              autoComplete="street-address"
              error={show(errors.addressLine)}
              testID="field-tiffin-address"
            />
            <Field
              label={t('Area')}
              value={address.area ?? ''}
              onChangeText={(value) => draft.setAddress({ area: value })}
              placeholder={t('e.g. Johar Town')}
              testID="field-tiffin-area"
            />
            <SelectField
              label={t('City')}
              required
              icon="location"
              options={cities.map((city) => ({ value: city, label: t(city) }))}
              value={address.city || null}
              onChange={(value) => draft.setAddress({ city: value })}
              disabled={cities.length === 1}
              error={show(errors.city)}
              testID="tiffin-city"
            />
            <Field
              label={t('Landmark')}
              value={address.landmark ?? ''}
              onChangeText={(value) => draft.setAddress({ landmark: value })}
              placeholder={t('e.g. Near the park')}
              testID="field-tiffin-landmark"
            />
          </View>
        </Card>
      ) : null}

      <Card title={t('Note for the kitchen')} style={styles.card} testID="tiffin-note">
        <Field
          label={t('Note')}
          icon="note"
          value={note}
          onChangeText={draft.setNote}
          placeholder={t('e.g. Less spicy, ring twice')}
          multiline
          maxLength={500}
          testID="field-tiffin-note"
        />
      </Card>
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: space.lg,
  },
  caption: {
    marginTop: space.sm,
  },
  fields: {
    gap: space.md,
  },
});
